import { IPublicClientApplication } from "@azure/msal-browser";
import { env } from "constants/envConfig";
import { internalFetch } from "./internalApi";

type APIResponse<T> = { data: T };

type AppRef = { id: string; name: string };

export type LinearState = {
  id: string;
  name: string;
  type?: string;
  color?: string;
  position?: number;
};

export type LinearIssueSummary = {
  id: string;
  identifier: string;
  title: string;
  url?: string;
  priority?: number;
  updatedAt?: string;
  state: { id: string; name: string; type?: string; color?: string };
};

export type LinearComment = {
  id: string;
  body: string;
  createdAt: string;
  user?: { id: string; name: string };
  botActor?: { userDisplayName: string; name: string };
};

export type LinearIssueDetail = LinearIssueSummary & {
  description?: string;
  createdAt?: string;
  comments: LinearComment[];
  creator?: { id: string; name: string };
  botActor?: { userDisplayName: string; name: string };
};

export async function getMe(
  msal: IPublicClientApplication,
  scopes: string[],
): Promise<{ admin: boolean }> {
  const res = await internalFetch<APIResponse<{ admin: boolean }>>({
    msal,
    scopes,
    url: `${env.REACT_APP_API_URL}/internal/me`,
  });
  return res.data;
}

export async function generateShareLink(
  msal: IPublicClientApplication,
  scopes: string[],
  params: { issueId: string; expiresAt?: string },
): Promise<{ url: string }> {
  const res = await internalFetch<APIResponse<{ url: string }>>({
    msal,
    scopes,
    url: `${env.REACT_APP_API_URL}/internal/share/generate`,
    method: "POST",
    body: params,
  });
  return res.data;
}

export async function getAccessibleApps(
  msal: IPublicClientApplication,
  scopes: string[],
) {
  const res = await internalFetch<APIResponse<{ applications: AppRef[] }>>({
    msal,
    scopes,
    url: `${env.REACT_APP_API_URL}/internal/apps`,
  });
  return res.data.applications;
}

export async function getBoard(
  msal: IPublicClientApplication,
  scopes: string[],
  applicationId: string,
) {
  const res = await internalFetch<
    APIResponse<{
      application: AppRef;
      states: LinearState[];
      issuesByState: Record<string, LinearIssueSummary[]>;
    }>
  >({
    msal,
    scopes,
    url: `${env.REACT_APP_API_URL}/internal/apps/${applicationId}/board`,
  });
  return res.data;
}

export async function getIssue(
  msal: IPublicClientApplication,
  scopes: string[],
  applicationId: string,
  issueId: string,
) {
  const res = await internalFetch<APIResponse<{ issue: LinearIssueDetail }>>({
    msal,
    scopes,
    url: `${env.REACT_APP_API_URL}/internal/apps/${applicationId}/issues/${issueId}`,
  });
  console.log("Fetched issue:", JSON.stringify(res.data, null, 2));
  return res.data.issue;
}

export async function createIssue(
  msal: IPublicClientApplication,
  scopes: string[],
  applicationId: string,
  payload: { title: string; description?: string; files?: File[] },
) {
  const formData = new FormData();
  formData.append("title", payload.title);
  if (payload.description) formData.append("description", payload.description);

  if (payload.files) {
    payload.files.forEach((file) => formData.append("files", file));
  }

  const res = await internalFetch<
    APIResponse<{ issue: { id: string; identifier: string } }>
  >({
    msal,
    scopes,
    url: `${env.REACT_APP_API_URL}/internal/apps/${applicationId}/issues`,
    method: "POST",
    body: formData,
  });
  return res.data.issue;
}

export async function createComment(
  msal: IPublicClientApplication,
  scopes: string[],
  applicationId: string,
  issueId: string,
  payload: { body: string; files?: File[] },
) {
  const formData = new FormData();
  formData.append("body", payload.body);

  if (payload.files) {
    payload.files.forEach((file) => formData.append("files", file));
  }

  const res = await internalFetch<APIResponse<{ comment: LinearComment }>>({
    msal,
    scopes,
    url: `${env.REACT_APP_API_URL}/internal/apps/${applicationId}/issues/${issueId}/comments`,
    method: "POST",
    body: formData,
  });
  return res.data.comment;
}

export async function fetchInternalImage(
  msal: IPublicClientApplication,
  scopes: string[],
  applicationId: string,
  imageUrl: string,
): Promise<Blob> {
  const res = await internalFetch<Blob>({
    msal,
    scopes,
    url: `${env.REACT_APP_API_URL}/internal/apps/${applicationId}/image/${encodeURIComponent(imageUrl)}`,
  });
  return res;
}

export async function fetchInternalImages(
  msal: IPublicClientApplication,
  scopes: string[],
  applicationId: string,
  imageUrls: string[],
): Promise<Blob[]> {
  const blobs: Blob[] = [];
  for (const url of imageUrls) {
    const blob = await fetchInternalImage(msal, scopes, applicationId, url);
    blobs.push(blob);
  }
  return blobs;
}
